import CalendarInput from 'components/CalendarInput/CalendarInput'
import { CheckBox } from 'components/CheckBox/CheckBox'
import { Container, ContainerHeader, ContainerContent } from 'components/Containers/Containers'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { NavBar } from 'components/NavBar/NavBar'
import { ISelectValue } from 'constants/globalTypes'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { addResourceBooking } from 'features/operations/bookingSlice'
import { AppDispatch } from 'store'
import { Button, Flex, H1WithBack, PageWrapper } from 'styling/GlobalStyles'
import { TitleText } from 'styling/Typography'
import { IResourceBookingState } from 'types/operationBooking.interface'
import { bookingPriorities, holderTypeOptions, resourceTypeOptions } from '../bookingData'

export const AddBooking = () => {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const [state, setState] = useState<IResourceBookingState>({
        resourceType: null,
        resource: null,
        holderType: null,
        holder: null,
        priority: null,
        startAt: null,
        endAt: null,
        preemptLowerPriority: false,
    })

    const handleSelect = (v: ISelectValue, n: string) => {
        setState((prev) => {
            if (n === 'resourceType') return { ...prev, resourceType: v, resource: null }
            if (n === 'holderType') return { ...prev, holderType: v, holder: null }
            return { ...prev, [n]: v }
        })
    }

    const handleDate = (date: Date, name: string) => {
        setState((prev) => ({ ...prev, [name]: date }))
    }

    const handleCheckbox = () => {
        setState((prev) => ({ ...prev, preemptLowerPriority: !prev.preemptLowerPriority }))
    }

    const handleSave = async () => {
        if (!state.resourceType?.value || !state.resource?.value) {
            toast.warn('Resource is required')
            return
        }
        if (!state.holderType?.value || !state.holder?.value) {
            toast.warn('Holder is required')
            return
        }
        if (!state.priority?.value) {
            toast.warn('Priority is required')
            return
        }
        if (!state.startAt || !state.endAt) {
            toast.warn('Start and end are required')
            return
        }
        if (state.endAt <= state.startAt) {
            toast.warn('End must be after start')
            return
        }
        const resp = await dispatch(addResourceBooking(state))
        if (resp.meta.requestStatus === 'fulfilled') {
            navigate(`/operations/booking/${(resp.payload as any).id}`, { replace: true })
        }
    }

    const resourceLink = state.resourceType?.value === 1 ? 'users/basic' : 'machines'
    const resourceParser =
        state.resourceType?.value === 1
            ? (data: any) => data.data.map((i: any) => ({ label: i.fullName, value: i.id }))
            : (data: any) => data.data.map((i: any) => ({ label: i.name, value: i.id }))

    const holderLink = state.holderType?.value === 1 ? 'aim-operations/plan' : 'aim-operations/shift-program'

    return <>
        <NavBar />
        <PageWrapper noFooter>
            <H1WithBack arrow title='Add Booking' />
            <Flex gap='1rem' style={{ marginTop: '2rem' }}>
                <Container>
                    <ContainerHeader>
                        <TitleText>Resource</TitleText>
                    </ContainerHeader>
                    <ContainerContent>
                        <Flex gap='1rem' mb='16'>
                            <HonestSelectInput
                                options={resourceTypeOptions}
                                name='resourceType'
                                labelText='Resource type'
                                onChange={handleSelect}
                                defaultValue={state.resourceType?.value}
                                required
                                invalid={!state.resourceType?.value}
                            />
                            {state.resourceType?.value && (
                                <HonestSelectAsyncInput
                                    keyProp={state.resourceType.value}
                                    link={resourceLink}
                                    name='resource'
                                    labelText='Resource'
                                    onChange={handleSelect}
                                    defaultValue={state.resource?.label}
                                    parserFunction={resourceParser}
                                    required
                                    invalid={!state.resource?.value}
                                />
                            )}
                        </Flex>
                    </ContainerContent>
                </Container>
                <Container>
                    <ContainerHeader>
                        <TitleText>Holder</TitleText>
                    </ContainerHeader>
                    <ContainerContent>
                        <Flex gap='1rem' mb='16'>
                            <HonestSelectInput
                                options={holderTypeOptions}
                                name='holderType'
                                labelText='Holder type'
                                onChange={handleSelect}
                                defaultValue={state.holderType?.value}
                                required
                                invalid={!state.holderType?.value}
                            />
                            {state.holderType?.value && (
                                <HonestSelectAsyncInput
                                    keyProp={state.holderType.value}
                                    link={holderLink}
                                    name='holder'
                                    labelText='Holder'
                                    onChange={handleSelect}
                                    defaultValue={state.holder?.label}
                                    parserFunction={(data: any) => data.data.map((i: any) => ({ label: i.name, value: i.id }))}
                                    required
                                    invalid={!state.holder?.value}
                                />
                            )}
                        </Flex>
                    </ContainerContent>
                </Container>
            </Flex>
            <Container style={{ marginTop: '1rem' }}>
                <ContainerHeader>
                    <TitleText>Window</TitleText>
                </ContainerHeader>
                <ContainerContent>
                    <HonestSelectInput
                        options={bookingPriorities}
                        name='priority'
                        labelText='Priority'
                        onChange={handleSelect}
                        defaultValue={state.priority?.value}
                        required
                        invalid={!state.priority?.value}
                    />
                    <Flex gap='1rem' mt='16' mb='16'>
                        <CalendarInput
                            width='100%'
                            name='startAt'
                            labelText='Start'
                            defaultValue={state.startAt}
                            minDate={new Date()}
                            onChange={handleDate}
                            required
                        />
                        <CalendarInput
                            width='100%'
                            name='endAt'
                            labelText='End'
                            defaultValue={state.endAt}
                            minDate={state.startAt || new Date()}
                            onChange={handleDate}
                            required
                        />
                    </Flex>
                    <CheckBox state={!!state.preemptLowerPriority} title='Preempt lower priority' square onClick={handleCheckbox} />
                </ContainerContent>
            </Container>
            <Flex gap='1rem' mt='32' justend>
                <Button onClick={() => navigate(-1)}>Back</Button>
                <Button orange onClick={handleSave}>Save</Button>
            </Flex>
        </PageWrapper>
    </>
}
